import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Briefcase, GraduationCap, ArrowRight, Award, Coins, ListChecks, Video, Car, MapPin, Home, Landmark } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ProfessionalProfile, MyHabits } from "@/pages/Personal";
import UserReports from "@/components/UserReports";
import UserOutputs from "@/components/UserOutputs";
import TrendingStrip from "@/components/TrendingStrip";
import JobBoard from "@/components/JobBoard";

export default function Professional() {
  const { user, setUser } = useApp();
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Briefcase size={14} /> Professional</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Your professional profile</h1>
        <p className="text-sec mt-2 text-sm">Your professional identity, done for you. Upload your resume and My Kind builds a polished public CV — experience, education, skills, projects, links and certificates from VishwaGuru-University. Get discovered, share one link, and let your work speak. One profile photo, zero busywork.</p>
      </motion.div>

      <ProfessionalProfile user={user} setUser={setUser} />

      {user && <JobBoard />}
      {user && <TrendingStrip scope="professional" title="Trending in Professional" />}

      <div className="mt-8" data-testid="community-power">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Landmark size={14} /> The Community Power</div>
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight mt-2">Tap into the whole community</h2>
        <p className="text-sec text-sm mt-1">One tap into every community power — learn, move, travel and stay together.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
          {[
            { to: "/learn", label: "University", icon: GraduationCap },
            { to: "/transport", label: "Rides", icon: Car },
            { to: "/transport?tab=tours", label: "Travel", icon: MapPin },
            { to: "/transport?tab=stays", label: "Stays", icon: Home },
          ].map((x) => (
            <Link key={x.label} to={x.to} data-testid={`power-${x.label.toLowerCase()}`}
              className="rud-card rounded-xl p-4 flex flex-col items-center gap-2 text-center hover:ring-1 hover:ring-brand/40 transition group">
              <x.icon size={20} className="text-brand" />
              <span className="text-xs font-medium">{x.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <Link to="/learn" data-testid="professional-learn-earn" className="group flex items-center gap-4 rud-card rounded-2xl p-5 mt-6 hover:border-brand/50 transition-colors">
        <div className="w-12 h-12 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><GraduationCap size={22} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand">Learn &amp; Earn · VishwaGuru-University</div>
          <div className="font-medium mt-0.5">Take or launch a course, ace competitive exams, earn certificates &amp; credits</div>
          <div className="text-xs text-sec mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1"><Award size={12} className="text-brand" /> Shareable certificates</span>
            <span className="flex items-center gap-1"><Coins size={12} className="text-brand" /> Earn credits when others learn from your link</span>
          </div>
        </div>
        <ArrowRight size={18} className="text-brand shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      <Link to="/meet" data-testid="professional-secure-meet" className="group flex items-center gap-4 rud-card rounded-2xl p-5 mt-4 hover:border-brand/50 transition-colors">
        <div className="w-12 h-12 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Video size={22} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand">Secure Meet · Encrypted video</div>
          <div className="font-medium mt-0.5">Host a private, end-to-end secure client or team call — invite anyone with one link</div>
          <div className="text-xs text-sec mt-1">No account required for guests. Perfect for interviews, client pitches and standups.</div>
        </div>
        <ArrowRight size={18} className="text-brand shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      {user && (
        <div className="mt-8" data-testid="professional-habits">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ListChecks size={14} /> Professional Habits</div>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight mt-2">Build the habits that grow your career</h2>
          <p className="text-sec text-sm mt-1">Track daily professional habits — productivity, leadership, skill-building — pull them from the best career books, set goals with an AI action plan, and keep your streak alive.</p>
          <MyHabits scope="professional" />
        </div>
      )}

      {user && <div className="mt-6"><UserReports ownerId={user.user_id} isOwner title="Your Search & Ask reports" /></div>}
      {user && <div className="mt-4"><UserOutputs ownerId={user.user_id} isOwner category="professional" title="Your professional saved outputs — Save & Earn" /></div>}
    </div>
  );
}
